import type { FC, FormEvent, ReactNode } from 'react'
import type { EnquiryFieldErrors, Service } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Check, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { contact } from '@/constants/site'

interface EnquiryFormProps {
    services: Pick<Service, 'slug' | 'title'>[]
    sourcePage?: string
}

const budgetBands = [
    'Under £25,000',
    '£25,000 to £50,000',
    '£50,000 to £100,000',
    '£100,000 or more',
    'Not sure yet'
]

const startWindows = [
    'As soon as possible',
    'Within three months',
    'Three to six months',
    'Later than six months',
    'Still planning'
]

const emptyValues = {
    full_name: '',
    email: '',
    phone: '',
    postcode: '',
    project_type: '',
    budget_band: '',
    start_window: '',
    message: ''
}

type Values = typeof emptyValues

const validate = (values: Values): EnquiryFieldErrors => {
    const errors: EnquiryFieldErrors = {}

    if (!values.full_name.trim()) {
        errors.full_name = 'Please tell us your name so we know who to ask for.'
    }

    if (!values.email.trim()) {
        errors.email = 'We need an email address to send the quotation to.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) {
        errors.email = 'That email address is missing something. Check it and try again.'
    }

    if (values.phone.trim() && values.phone.replace(/[^0-9]/g, '').length < 9) {
        errors.phone = 'That number looks too short. Include the area code.'
    }

    if (!values.postcode.trim()) {
        errors.postcode = 'We need the postcode to confirm we cover the address.'
    }

    if (!values.message.trim()) {
        errors.message = 'Tell us briefly what you are hoping to do with the property.'
    } else if (values.message.trim().length < 15) {
        errors.message = 'A sentence or two more will let us give you a useful answer.'
    }

    return errors
}

const fieldClasses = (invalid: boolean): string => cn(
    'w-full rounded-sm border bg-paper-raised px-4 py-3 text-[0.95rem] text-ink',
    'placeholder:text-muted-foreground/70 transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-minium focus:ring-offset-2 focus:ring-offset-paper',
    invalid ? 'border-destructive' : 'border-rule hover:border-ink/40'
)

const labelClasses = 'block font-mono text-[0.62rem] uppercase tracking-label text-muted-foreground'

export const EnquiryForm: FC<EnquiryFormProps> = ({
    services,
    sourcePage = '/contact'
}): ReactNode => {
    const [values, setValues] = useState<Values>(emptyValues)
    const [errors, setErrors] = useState<EnquiryFieldErrors>({})
    const [touched, setTouched] = useState<Record<string, boolean>>({})
    const [submitted, setSubmitted] = useState(false)
    const [pending, setPending] = useState(false)
    const [done, setDone] = useState(false)

    const mountedAt = useRef<number>(0)
    const errorRef = useRef<HTMLParagraphElement | null>(null)

    useEffect(() => {
        mountedAt.current = Date.now()
    }, [])

    const setField = (name: keyof Values, value: string): void => {
        setValues((current) => ({ ...current, [name]: value }))

        if (submitted || touched[name]) {
            setErrors(validate({ ...values, [name]: value }))
        }
    }

    const onBlur = (name: keyof Values): void => {
        setTouched((current) => ({ ...current, [name]: true }))
        setErrors(validate(values))
    }

    const showError = (name: keyof EnquiryFieldErrors): string | undefined => {
        if (!submitted && !touched[name as string]) return undefined

        return errors[name]
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        setSubmitted(true)

        const found = validate(values)
        setErrors(found)

        if (Object.keys(found).length) {
            const first = document.querySelector<HTMLElement>('[aria-invalid="true"]')
            first?.focus()

            return
        }

        setPending(true)

        try {
            const response = await fetch('/api/enquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    source_page: sourcePage,
                    company_website: '',
                    elapsed_ms: Date.now() - mountedAt.current
                })
            })

            const payload = await response.json().catch(() => ({}))

            if (!response.ok) {
                setErrors(payload?.errors ?? {
                    form: `We could not send that just now. Please call us on ${contact.phone} and we will take the details over the phone.`
                })
                setPending(false)
                window.requestAnimationFrame(() => errorRef.current?.focus())

                return
            }

            setDone(true)
            setPending(false)
        } catch {
            setErrors({
                form: 'We could not send that just now. Please call us on 020 8064 3301 and we will take the details over the phone.'
            })
            setPending(false)
        }
    }

    if (done) {
        return (
            <div className='border-t-2 border-minium bg-paper-raised p-8 sm:p-10'>
                <span className='grid size-11 place-items-center bg-minium text-white'>
                    <Check aria-hidden='true' className='size-5' />
                </span>

                <h2 className='mt-6 font-heading text-2xl font-bold tracking-title text-ink'>
                    Thank you, that has reached us
                </h2>

                <p className='mt-4 max-w-lg leading-relaxed text-muted-foreground'>
                    We read every enquiry ourselves. You will get a reply from Capital Craft within
                    one working day, and if the project looks like a fit we will suggest two dates
                    for a free site visit.
                </p>

                <p className='mt-4 max-w-lg leading-relaxed text-muted-foreground'>
                    If it is urgent, call {contact.phone} between 08:00 and 18:00 on a weekday and
                    ask for the site office.
                </p>
            </div>
        )
    }

    return (
        <form
            action='/api/enquiries'
            method='post'
            onSubmit={onSubmit}
            noValidate
            className='border-t-2 border-ink pt-8'
        >
            <input type='hidden' name='source_page' value={sourcePage} />

            <p aria-hidden='true' className='absolute left-[-9999px] h-px w-px overflow-hidden'>
                <label htmlFor='company_website'>Leave this field empty</label>
                <input
                    id='company_website'
                    name='company_website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                />
            </p>

            {errors.form ? (
                <p
                    ref={errorRef}
                    tabIndex={-1}
                    role='alert'
                    className='mb-8 flex items-start gap-3 border border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive'
                >
                    <AlertCircle aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                    {errors.form}
                </p>
            ) : null}

            <div className='grid gap-6 sm:grid-cols-2'>
                <div className='sm:col-span-2'>
                    <label htmlFor='full_name' className={labelClasses}>
                        Your name
                    </label>

                    <input
                        id='full_name'
                        name='full_name'
                        type='text'
                        autoComplete='name'
                        required
                        value={values.full_name}
                        onChange={(event) => setField('full_name', event.target.value)}
                        onBlur={() => onBlur('full_name')}
                        aria-invalid={showError('full_name') ? 'true' : undefined}
                        aria-describedby={showError('full_name') ? 'full_name-error' : undefined}
                        className={cn('mt-2', fieldClasses(Boolean(showError('full_name'))))}
                    />

                    {showError('full_name') ? (
                        <p id='full_name-error' className='mt-2 text-sm text-destructive'>
                            {showError('full_name')}
                        </p>
                    ) : null}
                </div>

                <div>
                    <label htmlFor='email' className={labelClasses}>
                        Email
                    </label>

                    <input
                        id='email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        required
                        value={values.email}
                        onChange={(event) => setField('email', event.target.value)}
                        onBlur={() => onBlur('email')}
                        aria-invalid={showError('email') ? 'true' : undefined}
                        aria-describedby={showError('email') ? 'email-error' : undefined}
                        className={cn('mt-2', fieldClasses(Boolean(showError('email'))))}
                    />

                    {showError('email') ? (
                        <p id='email-error' className='mt-2 text-sm text-destructive'>
                            {showError('email')}
                        </p>
                    ) : null}
                </div>

                <div>
                    <label htmlFor='phone' className={labelClasses}>
                        Phone <span className='normal-case tracking-normal'>(optional)</span>
                    </label>

                    <input
                        id='phone'
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        value={values.phone}
                        onChange={(event) => setField('phone', event.target.value)}
                        onBlur={() => onBlur('phone')}
                        aria-invalid={showError('phone') ? 'true' : undefined}
                        aria-describedby={showError('phone') ? 'phone-error' : undefined}
                        className={cn('mt-2', fieldClasses(Boolean(showError('phone'))))}
                    />

                    {showError('phone') ? (
                        <p id='phone-error' className='mt-2 text-sm text-destructive'>
                            {showError('phone')}
                        </p>
                    ) : null}
                </div>

                <div>
                    <label htmlFor='postcode' className={labelClasses}>
                        Property postcode
                    </label>

                    <input
                        id='postcode'
                        name='postcode'
                        type='text'
                        autoComplete='postal-code'
                        required
                        value={values.postcode}
                        onChange={(event) => setField('postcode', event.target.value)}
                        onBlur={() => onBlur('postcode')}
                        aria-invalid={showError('postcode') ? 'true' : undefined}
                        aria-describedby={showError('postcode') ? 'postcode-error' : undefined}
                        className={cn('mt-2 uppercase', fieldClasses(Boolean(showError('postcode'))))}
                    />

                    {showError('postcode') ? (
                        <p id='postcode-error' className='mt-2 text-sm text-destructive'>
                            {showError('postcode')}
                        </p>
                    ) : null}
                </div>

                <div>
                    <label htmlFor='project_type' className={labelClasses}>
                        Type of work <span className='normal-case tracking-normal'>(optional)</span>
                    </label>

                    <select
                        id='project_type'
                        name='project_type'
                        value={values.project_type}
                        onChange={(event) => setField('project_type', event.target.value)}
                        className={cn('mt-2', fieldClasses(false))}
                    >
                        <option value=''>Not sure yet</option>

                        {services.map((service) => (
                            <option key={service.slug} value={service.slug}>
                                {service.title}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label htmlFor='budget_band' className={labelClasses}>
                        Rough budget <span className='normal-case tracking-normal'>(optional)</span>
                    </label>

                    <select
                        id='budget_band'
                        name='budget_band'
                        value={values.budget_band}
                        onChange={(event) => setField('budget_band', event.target.value)}
                        className={cn('mt-2', fieldClasses(false))}
                    >
                        <option value=''>Prefer not to say</option>

                        {budgetBands.map((band) => (
                            <option key={band} value={band}>
                                {band}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label htmlFor='start_window' className={labelClasses}>
                        When you want to start <span className='normal-case tracking-normal'>(optional)</span>
                    </label>

                    <select
                        id='start_window'
                        name='start_window'
                        value={values.start_window}
                        onChange={(event) => setField('start_window', event.target.value)}
                        className={cn('mt-2', fieldClasses(false))}
                    >
                        <option value=''>Not decided</option>

                        {startWindows.map((window) => (
                            <option key={window} value={window}>
                                {window}
                            </option>
                        ))}
                    </select>
                </div>

                <div className='sm:col-span-2'>
                    <label htmlFor='message' className={labelClasses}>
                        What are you planning?
                    </label>

                    <textarea
                        id='message'
                        name='message'
                        rows={6}
                        required
                        value={values.message}
                        onChange={(event) => setField('message', event.target.value)}
                        onBlur={() => onBlur('message')}
                        placeholder='For example: Victorian terrace, we want to fill the side return and open the ground floor into one room. Drawings not done yet.'
                        aria-invalid={showError('message') ? 'true' : undefined}
                        aria-describedby={showError('message') ? 'message-error' : undefined}
                        className={cn('mt-2 resize-y', fieldClasses(Boolean(showError('message'))))}
                    />

                    {showError('message') ? (
                        <p id='message-error' className='mt-2 text-sm text-destructive'>
                            {showError('message')}
                        </p>
                    ) : null}
                </div>
            </div>

            <div className='mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between'>
                <button
                    type='submit'
                    disabled={pending}
                    className={cn(
                        'inline-flex items-center justify-center gap-2.5 rounded-sm border border-minium bg-minium px-7 py-4',
                        'text-[0.95rem] font-medium text-white transition-colors duration-200',
                        'hover:bg-minium-hover hover:border-minium-hover',
                        'disabled:cursor-not-allowed disabled:opacity-70'
                    )}
                >
                    {pending ? (
                        <Loader2 aria-hidden='true' className='size-4 animate-spin' />
                    ) : null}

                    {pending ? 'Sending' : 'Send enquiry'}
                </button>

                <p className='max-w-xs text-xs leading-relaxed text-muted-foreground'>
                    We reply within one working day. Your details are used to answer this enquiry
                    and nothing else.
                </p>
            </div>
        </form>
    )
}
